import { CarIcon, CheckIcon, HomeIcon, InfoIcon, Share2Icon, SquarePenIcon, UserCogIcon } from 'lucide-react'

import { JourneyPan, JourneyView, PhoneJourneyStage, useTarget } from '../../journey'

import {
  CONFIRM_MESSAGE,
  HIGHLIGHT_OPTIONS,
  isRowHidden,
  PERMISSIONS,
  PLATFORM_BADGE,
  PLATFORM_OPTIONS,
  STAR_BADGE,
  TEAM,
  toneClass,
  VEHICLES,
} from './content'

import type { ReactNode } from 'react'
import type { ChileautosHighlightsState } from './journey'

/*
Veekls tal como se ve en un teléfono: el menú se apila arriba de la página, los
permisos van en una sola columna, los filtros uno por línea, la tabla de
Publicaciones sigue más allá del borde derecho y cada select abre el selector
del sistema desde abajo. La pista del permiso no aparece: en el perfil real es
un texto al pasar el mouse, y un teléfono no tiene mouse. Por eso lo dice la
leyenda.
*/

interface ViewProps {
  state: ChileautosHighlightsState
}

export const MobileSimulator = ({ state }: ViewProps) => (
  <PhoneJourneyStage
    state={state}
    user={state.seller ? { name: 'Camilo', role: 'Vendedor' } : { name: 'Marcelo', role: 'Administrador' }}
    badge={state.relogged ? 'Volvió a iniciar sesión' : null}
  >
    <HomeView state={state} />
    <LoadingView />
    <UsersView />
    <PermissionsView state={state} />
    <PublishView state={state} />

    <ConfirmDialog open={state.confirmOpen} />

    <PickerSheet title="Plataformas" open={state.menu === 'platform'}>
      {['-- Todas --', ...PLATFORM_OPTIONS].map((option) => (
        <PickerOption
          key={option}
          targetKey={option === 'ChileAutos' ? 'optionChileautos' : undefined}
          label={option}
          hovered={option === 'ChileAutos' && state.hoveredOption === 'chileautos'}
        />
      ))}
    </PickerSheet>
    <PickerSheet title="Destacado" open={state.menu === 'highlight'}>
      {HIGHLIGHT_OPTIONS.map((option) => (
        <PickerOption
          key={option.value}
          targetKey={option.value === 'topspot' ? 'optionTopspot' : undefined}
          label={option.label}
          hovered={option.value === 'topspot' && state.hoveredOption === 'topspot'}
          compact
        />
      ))}
    </PickerSheet>

    <div
      className="absolute bottom-3 left-1/2 z-50 flex items-center gap-1.5 whitespace-nowrap rounded-md bg-[#1c1917] px-2.5 py-1.5 text-[10.5px] text-white transition-all duration-300"
      style={{
        opacity: state.toast ? 1 : 0,
        transform: `translate(-50%, ${state.toast ? '0' : '0.75rem'})`,
      }}
    >
      <CheckIcon size={12} className="text-[#22c55e]" />
      <span>{state.toast ?? ''}</span>
    </div>
  </PhoneJourneyStage>
)

const NAV = [
  { key: 'inicio', label: 'Inicio', icon: HomeIcon },
  { key: 'vehiculos', label: 'Vehículos', icon: CarIcon },
  { key: 'publicaciones', label: 'Publicaciones', icon: Share2Icon, target: 'navPublicaciones' },
  { key: 'usuarios', label: 'Usuarios', icon: UserCogIcon, target: 'navUsuarios' },
] as const

const HomeView = ({ state }: ViewProps) => (
  <JourneyView name="home" className="bg-white p-2.5">
    <div className="space-y-0.5 rounded-lg border border-[#e4e1e4] p-1">
      {NAV.map((item) => (
        <NavRow key={item.key} item={item} active={state.navActive === item.key} />
      ))}
    </div>

    <h3 className="mt-3 text-[13px] font-bold text-[#1c1917]">Hola, Automotora Central</h3>
    <p className="mb-2 text-[9.5px] text-[#706d71]">Resumen del mes en curso</p>
    <div className="grid grid-cols-3 gap-1.5">
      {[
        ['Ventas', '12'],
        ['Publicados', '36'],
        ['Consultas', '21'],
      ].map(([label, value]) => (
        <div key={label} className="rounded border border-[#e4e1e4] bg-[#faf8f8] p-2">
          <p className="font-mono text-[8px] uppercase text-[#706d71]">{label}</p>
          <p className="mt-0.5 text-[14px] font-bold text-[#1c1917]">{value}</p>
        </div>
      ))}
    </div>
    <div className="mt-2 space-y-1.5 rounded border border-[#e4e1e4] p-2">
      {[100, 80, 60].map((width) => (
        <div key={width} className="h-2 animate-pulse rounded bg-stone-200" style={{ width: `${width}%` }} />
      ))}
    </div>
  </JourneyView>
)

const NavRow = ({ item, active }: { item: (typeof NAV)[number]; active: boolean }) => {
  const ref = useTarget('target' in item ? item.target : '')
  const Icon = item.icon

  return (
    <div
      ref={'target' in item ? ref : undefined}
      className={`flex items-center gap-2 rounded px-2 py-[6px] text-[11px] transition-all duration-200 ${
        active ? 'bg-[#e5484d]/10 font-medium text-[#e5484d]' : 'text-[#44403c]'
      }`}
    >
      <Icon size={14} />
      <span>{item.label}</span>
    </div>
  )
}

const LoadingView = () => (
  <JourneyView name="loading" className="bg-white p-3">
    <div className="mb-3 h-3 w-1/2 animate-pulse rounded bg-stone-200" />
    <div className="space-y-2">
      {[100, 92, 80, 100, 66].map((width, index) => (
        <div key={index} className="h-2.5 animate-pulse rounded bg-stone-200" style={{ width: `${width}%` }} />
      ))}
    </div>
  </JourneyView>
)

const UsersView = () => {
  const member = useTarget('member')

  return (
    <JourneyView name="users" className="bg-white p-2.5">
      <h3 className="text-[13px] font-bold text-[#1c1917]">Gestionar Usuarios</h3>
      <p className="mb-2 text-[9.5px] text-[#706d71]">Miembros de Automotora Central</p>
      <div className="divide-y divide-[#f2eeee] rounded-lg border border-[#e4e1e4]">
        {TEAM.map((person) => (
          <div
            key={person.name}
            ref={person.primary ? member : undefined}
            className="flex items-center justify-between px-2.5 py-2"
          >
            <div>
              <p className={`text-[11px] text-[#1c1917] ${person.primary ? 'font-medium' : ''}`}>{person.name}</p>
              <p className="text-[9px] text-[#706d71]">{person.job}</p>
            </div>
            <span className="text-[9px] text-[#107c41]">{person.state}</span>
          </div>
        ))}
      </div>
    </JourneyView>
  )
}

const PermissionsView = ({ state }: ViewProps) => {
  const permissionBox = useTarget('permissionBox')
  const save = useTarget('save')

  return (
    <JourneyView name="permissions" className="bg-white">
      <JourneyPan to={state.permissionsPan} align={0.45}>
        <div className="p-2.5">
          <h3 className="text-[13px] font-bold text-[#1c1917]">Camilo Rojas</h3>
          <p className="mb-2 text-[9.5px] text-[#706d71]">Vendedor · Automotora Central</p>
          <div className="mb-2.5 space-y-1.5">
            <div className="h-6 animate-pulse rounded bg-stone-200" />
            <div className="h-6 animate-pulse rounded bg-stone-200" />
          </div>

          <p className="mb-1 font-mono text-[8.5px] uppercase text-[#706d71]">Permisos</p>
          {PERMISSIONS.map((permission) => {
            const checked = permission.isTarget ? state.permissionChecked : permission.checked

            return (
              <div
                key={permission.label}
                ref={permission.isTarget ? permissionBox : undefined}
                className={`vk-perm-cell flex items-center gap-2 rounded-md px-2 py-[7px] ${toneClass[permission.tone]}`}
                data-highlighted={permission.isTarget && state.permissionHighlighted}
              >
                <span
                  className={`vk-perm-box flex h-[13px] w-[13px] shrink-0 items-center justify-center rounded-[3px] border ${
                    checked ? 'border-[#e5484d]' : 'border-stone-300 bg-white'
                  }`}
                  data-checked={checked}
                  data-tone="red"
                >
                  {checked && <CheckIcon size={10} className="text-white" strokeWidth={4} />}
                </span>
                <span
                  className={`text-[9.5px] uppercase leading-tight tracking-[0.01em] ${
                    permission.isTarget ? 'font-medium' : ''
                  }`}
                >
                  {permission.label}
                  {permission.isTarget && (
                    <InfoIcon size={10} className="ml-1 inline-block align-text-top text-[#a8a29e]" />
                  )}
                </span>
              </div>
            )
          })}

          <div className="flex items-center justify-center gap-2 pb-4 pt-3">
            <span ref={save} className="rounded bg-[#3b82f6] px-3 py-1.5 text-[10.5px] font-bold text-white">
              Grabar cambios
            </span>
            <span className="rounded bg-[#3b82f6] px-3 py-1.5 text-[10.5px] font-bold text-white">Cancelar</span>
          </div>
        </div>
      </JourneyPan>
    </JourneyView>
  )
}

const ROW_GRID = 'grid grid-cols-[12px_14px_50px_1fr_auto] items-center gap-2'

const PublishView = ({ state }: ViewProps) => {
  const filtersToggle = useTarget('filtersToggle')
  const platformSelect = useTarget('platformSelect')
  const highlightSelect = useTarget('highlightSelect')

  return (
    <JourneyView name="publish" className="bg-white">
      <JourneyPan to={state.publishPan} align={0.6}>
        <div className="p-2.5">
          <h3 className="text-[13px] font-bold leading-none text-[#1c1917]">Vehículos Publicados</h3>
          <p className="mt-1 text-[9.5px] text-[#706d71]">Plataformas Externas</p>
          <p ref={filtersToggle} className="mt-1.5 inline-block text-[10px] font-bold text-[#2563eb]">
            {state.filtersOpen ? 'Esconder filtros de búsqueda' : 'Ver filtros de búsqueda'}
          </p>

          {/* One filter per line on a phone */}
          <div
            className="vk-fold"
            style={{ maxHeight: state.filtersOpen ? 160 : 0, opacity: state.filtersOpen ? 1 : 0 }}
          >
            <div className="space-y-1.5 pt-2">
              <div>
                <p className="mb-0.5 font-mono text-[8.5px] uppercase text-[#706d71]">Plataformas</p>
                <PhoneSelect
                  targetRef={platformSelect}
                  value={state.platformChosen ? 'ChileAutos' : null}
                  placeholder="-- Todas --"
                  focused={state.menu === 'platform'}
                />
              </div>
              <div
                className="vk-fold"
                style={{
                  maxHeight: state.platformChosen ? 60 : 0,
                  opacity: state.platformChosen ? 1 : 0,
                  transform: state.platformChosen ? 'none' : 'translateY(-6px) scale(0.96)',
                }}
              >
                <p className="mb-0.5 font-mono text-[8.5px] uppercase text-[#e5484d]">Destacado</p>
                <PhoneSelect
                  targetRef={highlightSelect}
                  value={state.highlightChosen ? 'TopSpot' : null}
                  placeholder="-- Todos --"
                  focused={state.menu === 'highlight'}
                />
              </div>
              <div className="inline-block rounded border border-[#e5484d] px-2 py-1 text-[9.5px] text-[#e5484d]">
                Limpiar Filtros
              </div>
            </div>
          </div>

          {/* Wider than the phone, so it scrolls sideways: the fade says so */}
          <div className="relative mt-2 overflow-hidden rounded border border-[#e4e1e4]">
            <div className={`${ROW_GRID} border-b border-[#e4e1e4] bg-[#faf8f8] px-2 py-1.5`}>
              <span className="h-2.5 w-2.5 rounded-sm border border-[#d0cbd0]" />
              <span />
              {['Patente', 'Vehículo'].map((column) => (
                <span key={column} className="font-mono text-[8px] uppercase text-[#706d71]">
                  {column}
                </span>
              ))}
              <span className="pr-3 font-mono text-[8px] uppercase text-[#706d71]">Plataformas</span>
            </div>

            {VEHICLES.map((vehicle) => (
              <VehicleRow key={vehicle.plate} vehicle={vehicle} state={state} />
            ))}

            <div className="pointer-events-none absolute inset-y-0 right-0 w-[18px] bg-gradient-to-r from-white/0 to-white" />
          </div>
        </div>
      </JourneyPan>
    </JourneyView>
  )
}

const VehicleRow = ({ vehicle, state }: { vehicle: (typeof VEHICLES)[number]; state: ChileautosHighlightsState }) => {
  const editRow = useTarget('editRow')
  const hidden = isRowHidden(vehicle.hiddenBy, state)
  const star = vehicle.isTarget ? (state.applied ? 'TopSpot' : undefined) : vehicle.star

  return (
    <>
      <div
        className={`vk-fold ${ROW_GRID} border-[#f2eeee] px-2 ${hidden ? 'border-b-0' : 'border-b'}`}
        style={{
          maxHeight: hidden ? 0 : 48,
          opacity: hidden ? 0 : 1,
          paddingTop: hidden ? 0 : 8,
          paddingBottom: hidden ? 0 : 8,
          backgroundColor: vehicle.isTarget && state.editorOpen ? 'rgba(229, 72, 77, 0.08)' : undefined,
        }}
      >
        <span className="h-2.5 w-2.5 rounded-sm border border-[#d0cbd0]" />
        <span ref={vehicle.isTarget ? editRow : undefined}>
          <SquarePenIcon size={12} className="text-[#e5484d]" />
        </span>
        <span className="text-[10px] font-bold">{vehicle.plate}</span>
        <span className="truncate text-[10px]">{vehicle.model}</span>
        <span className="flex gap-1 pr-3">
          {vehicle.platforms.map((platform) => (
            <span key={platform} className={`rounded px-1 text-[8.5px] ${PLATFORM_BADGE[platform]}`}>
              {platform}
            </span>
          ))}
          {star && (
            <span className={`rounded px-1 text-[8.5px] ${STAR_BADGE} ${vehicle.isTarget ? 'vk-badge-pop' : ''}`}>
              &#9733; {star}
            </span>
          )}
        </span>
      </div>

      {vehicle.isTarget && <RowEditor state={state} />}
    </>
  )
}

const RowEditor = ({ state }: ViewProps) => {
  const chaTab = useTarget('chaTab')
  const destacadosTab = useTarget('destacadosTab')

  return (
    <div
      className={`vk-fold border-[#e4e1e4] bg-white ${state.editorOpen ? 'border-b' : 'border-b-0'}`}
      style={{ maxHeight: state.editorOpen ? 260 : 0, opacity: state.editorOpen ? 1 : 0 }}
    >
      <div className="grid grid-cols-3 border-b border-[#f2eeee] text-center">
        {['MercadoLibre', 'ChileAutos', 'Yapo'].map((tab) => {
          const active = tab === 'ChileAutos' ? state.chaTab : tab === 'MercadoLibre' && !state.chaTab

          return (
            <span
              key={tab}
              ref={tab === 'ChileAutos' ? chaTab : undefined}
              className={`border-b-[1.5px] py-1.5 text-[10px] transition-all duration-200 ${
                active ? 'border-[#2563eb] text-[#2563eb]' : 'border-transparent text-[#706d71]'
              }`}
            >
              {tab}
            </span>
          )
        })}
      </div>
      <div
        className="flex gap-3 overflow-hidden whitespace-nowrap border-b border-[#f2eeee] px-2 pt-1.5"
        style={{ visibility: state.chaTab ? 'visible' : 'hidden' }}
      >
        <span className="pb-1 text-[9px] text-[#706d71]">Detalles del Vehículo</span>
        <span className="pb-1 text-[9px] text-[#706d71]">Imágenes (8)</span>
        <span
          ref={destacadosTab}
          className={`border-b-[1.5px] pb-1 text-[9px] ${
            state.destacadosTab ? 'border-[#ea580c] text-[#ea580c]' : 'border-transparent text-[#706d71]'
          }`}
        >
          Destacados
        </span>
      </div>

      {state.destacadosTab ? (
        <HighlightsPanel state={state} />
      ) : (
        <div className="space-y-1.5 px-2 py-2">
          {[66, 50, 60].map((width) => (
            <div key={width} className="h-2 animate-pulse rounded bg-stone-200" style={{ width: `${width}%` }} />
          ))}
        </div>
      )}
    </div>
  )
}

const HighlightsPanel = ({ state }: ViewProps) => {
  const apply = useTarget('apply')

  return (
    <div className="px-2 py-2">
      <div className="mb-1.5 flex items-center justify-between">
        <p className="text-[10.5px] font-bold text-[#1c1917]">Destacados en ChileAutos</p>
        <span className="rounded bg-[#fde8e8] px-1.5 py-0.5 text-[8.5px] text-[#e5484d]">Actualizar saldo</span>
      </div>
      <p className="mb-0.5 text-[9px] font-medium text-[#57534e]">En este aviso</p>
      {state.applied ? (
        <div className="vk-preview-fade mb-1.5 rounded border border-[#fde3b8] bg-[#fff8ec] px-2 py-1 text-[9.5px]">
          <span className="font-bold">TopSpot</span>
          <span className="text-[#706d71]"> · mensual · quedan 7 días</span>
        </div>
      ) : (
        <p className="mb-1.5 text-[9px] text-[#a8a29e]">Este aviso no tiene destacados aplicados desde Veekls.</p>
      )}
      <p className="mb-0.5 text-[9px] font-medium text-[#57534e]">Tus productos contratados</p>
      <div className="divide-y divide-[#f2eeee] rounded border border-[#f2eeee]">
        <div className="flex items-center justify-between gap-2 px-2 py-1.5">
          <div className="min-w-0">
            <p className="text-[10px] font-bold">Aviso Premium</p>
            <p className="text-[8.5px] text-[#706d71]">reutilizable · 3 de 3 disponibles</p>
          </div>
          {state.applied ? (
            <span className="max-w-[45%] text-right text-[8.5px] text-[#706d71]">
              Disponible cuando venza el TopSpot
            </span>
          ) : (
            <span className="shrink-0 rounded bg-[#e5484d] px-2.5 py-1 text-[9.5px] text-white">Aplicar</span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 px-2 py-1.5">
          <div className="min-w-0">
            <p className="text-[10px] font-bold">TopSpot</p>
            <p className="text-[8.5px] text-[#706d71]">
              mensual ·{' '}
              <span className={state.applied ? 'font-semibold text-[#e5484d]' : ''}>
                {state.applied ? '2 de 3 disponibles' : '3 de 3 disponibles'}
              </span>
            </p>
          </div>
          {state.applied ? (
            <span className="text-[8.5px] text-[#706d71]">Ya aplicado</span>
          ) : (
            <span ref={apply} className="shrink-0 rounded bg-[#e5484d] px-2.5 py-1 text-[9.5px] text-white">
              Aplicar
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

const ConfirmDialog = ({ open }: { open: boolean }) => {
  const confirmYes = useTarget('confirmYes')

  return (
    <div
      className="vk-pop absolute inset-0 z-40 flex items-center justify-center bg-black/50"
      style={{ opacity: open ? 1 : 0, pointerEvents: 'none' }}
    >
      <div
        className="vk-pop w-[82%] rounded-lg bg-white p-3.5 shadow-lg"
        style={{ transform: `scale(${open ? 1 : 0.92})` }}
      >
        <p className="mb-3 text-center text-[11px] leading-snug text-[#1c1917]">{CONFIRM_MESSAGE}</p>
        <div className="flex justify-around">
          <span ref={confirmYes} className="rounded bg-[#3b82f6] px-5 py-1.5 text-[11px] text-white">
            Si
          </span>
          <span className="rounded bg-[#d1d5db] px-5 py-1.5 text-[11px] text-[#1c1917]">No</span>
        </div>
      </div>
    </div>
  )
}

interface PhoneSelectProps {
  targetRef: (node: HTMLElement | null) => void
  value: string | null
  placeholder: string
  focused: boolean
}

const PhoneSelect = ({ targetRef, value, placeholder, focused }: PhoneSelectProps) => (
  <div
    ref={targetRef}
    className="flex justify-between rounded-[3px] border bg-white px-2 py-1.5 text-[10.5px] transition-all duration-200"
    style={{
      borderColor: focused ? '#e5484d' : '#e4e1e4',
      boxShadow: focused ? '0 0 0 2px rgba(229, 72, 77, 0.08)' : 'none',
    }}
  >
    <span className={value ? 'font-medium text-[#1c1917]' : 'text-[#706d71]'}>{value ?? placeholder}</span>
    <span className="text-[#a8a29e]">&#9662;</span>
  </div>
)

/** What a native select opens on a phone: the system picker, from below. */
const PickerSheet = ({ title, open, children }: { title: string; open: boolean; children: ReactNode }) => (
  <div
    className="vk-pop absolute inset-0 z-50 flex items-end bg-black/40"
    style={{ opacity: open ? 1 : 0, pointerEvents: 'none' }}
  >
    <div
      className="vk-pop w-full rounded-t-xl bg-white pb-3 pt-2"
      style={{ transform: `translateY(${open ? '0' : '100%'})` }}
    >
      <div className="mx-auto mb-2 h-1 w-8 rounded-full bg-[#d0cbd0]" />
      <p className="mb-1 px-3 font-mono text-[9px] uppercase text-[#706d71]">{title}</p>
      {children}
    </div>
  </div>
)

interface PickerOptionProps {
  targetKey?: string
  label: string
  hovered: boolean
  compact?: boolean
}

const PickerOption = ({ targetKey, label, hovered, compact = false }: PickerOptionProps) => {
  const ref = useTarget(targetKey ?? '')

  return (
    <div
      ref={targetKey ? ref : undefined}
      className={`px-3 text-[11.5px] transition-colors ${compact ? 'py-[7px]' : 'py-2'} ${
        hovered ? 'bg-[#e5484d]/10 text-[#e5484d]' : ''
      }`}
    >
      {label}
    </div>
  )
}
